import React from 'react';
import { supabase } from '../supabase';

// NOTE: Fantasy NR is now ranked by FPTS/game (PPR) using per-player ESPN stats.
// The nr_formula_* fields below are kept for admin display and future use
// but no longer directly drive the NR ranking in StartSitTool.
export const CONFIG_DEFAULTS = {
  nr_formula_qb:  { passTd: 5, passYards: 0.04, int: -2, rushYards: 0.1, rushTd: 6, minAttempts: 100 },
  nr_formula_rb:  { rushYards: 0.1, rushTd: 6, receptions: 1, recYards: 0.1, recTd: 6, minCarries: 50 },
  nr_formula_wr:  { receptions: 1, recYards: 0.1, recTd: 6, minTargets: 20 },
  nr_formula_te:  { receptions: 1, recYards: 0.1, recTd: 6, minTargets: 15 },
  // Offensive NR stat weights (rank × weight before summing; higher = more influence)
  nr_weights_off_qb: { '=completionPct': 2, passingYards: 2, passingTouchdowns: 2, '=~interceptionPct': 1.5, '~sacks': 1, rushingYards: 0.5, rushingTouchdowns: 0.5, '=passing_epa': 2.5, '=cpoe': 2.0, '=~pressure_pct': 1.5 },
  nr_weights_off_rb: { rushingYards: 2, '=yardsPerRushAttempt': 1.5, rushingTouchdowns: 2, receivingYards: 1, '=yardsPerReception': 1, receivingTouchdowns: 1.5, '~fumbles': 1.5, '=rushing_epa': 2.0, '=ybc_att': 1.5, '=rush_yards_over_expected_per_att': 1.5 },
  nr_weights_off_wr: { receptions: 1.5, receivingYards: 2, receivingTouchdowns: 2, receivingFirstDowns: 1.5, rushingYards: 0.5, rushingTouchdowns: 0.5, '~fumbles': 1.5, '=receiving_epa': 2.0, '=avg_separation': 1.5, '=~drop_percent': 1.0 },
  nr_weights_off_te: { receptions: 1.5, receivingYards: 2, receivingTouchdowns: 2, receivingFirstDowns: 1.5, rushingYards: 0.5, rushingTouchdowns: 0.5, '~fumbles': 1.5, '=receiving_epa': 2.0, '=avg_separation': 1.5, '=~drop_percent': 1.0 },
  // Defensive NR stat weights
  nr_weights_def_de: { sacks: 2.5, QBHits: 1.5, tacklesForLoss: 1.5, totalTackles: 1, forcedFumbles: 1, '=prss': 2.0, '=hrry': 1.5 },
  nr_weights_def_dt: { sacks: 2, QBHits: 1.5, tacklesForLoss: 1.5, totalTackles: 1.5, forcedFumbles: 1, '=prss': 2.0, '=hrry': 1.5 },
  nr_weights_def_lb: { totalTackles: 2, sacks: 1.5, tacklesForLoss: 1.5, passesDefended: 1, forcedFumbles: 1, '=prss': 1.0, '=~m_tkl_percent': 1.5 },
  nr_weights_def_cb: { passesDefended: 2.5, interceptions: 2, totalTackles: 1, '=~yds_tgt': 2.0, '=~m_tkl_percent': 1.5 },
  nr_weights_def_s:  { totalTackles: 2, passesDefended: 1.5, interceptions: 2, '=~yds_tgt': 2.0, '=~m_tkl_percent': 1.5 },
  site_features:  { constructionBanner: true, fantasyHub: true, faExplorer: true, draftExplorer: true, capEfficiency: true, waiverWire: false },
  projection_settings: { matchupWeight: 0.01 },
  nr_weights_team_off: { ptsWeight: 1.5 }, // weight for points-scored rank in Team Offense NR
  nr_weights_team_def: { ptsWeight: 1.5 }, // weight for points-allowed rank in Team Defense NR
};

// Module-level cache — only fetched once per app session
let cachedConfig = null;
let fetchInProgress = null;

// In-process subscribers so same-tab useConfig hooks reload when admin saves
const _subscribers = new Set();

/** Call after admin saves a config row to force all useConfig consumers to reload. */
export function bustConfigCache() {
  cachedConfig = null;
  fetchInProgress = null;
  // Notify same-tab subscribers immediately
  _subscribers.forEach(cb => cb());
  // Broadcast to other tabs via storage event
  try { localStorage.setItem('nr_config_updated', String(Date.now())); } catch (_) {}
}

function loadConfig() {
  if (cachedConfig) return Promise.resolve(cachedConfig);
  if (!fetchInProgress) {
    fetchInProgress = supabase
      .from('site_config')
      .select('key, value')
      .then(({ data, error }) => {
        fetchInProgress = null;
        if (error) throw error;
        const result = { ...CONFIG_DEFAULTS };
        for (const row of data || []) {
          // For site_features, merge with defaults so newly-added keys are always present
          if (row.key === 'site_features') {
            result[row.key] = { ...CONFIG_DEFAULTS.site_features, ...row.value };
          } else {
            result[row.key] = row.value;
          }
        }
        cachedConfig = result;
        return result;
      })
      .catch(err => {
        fetchInProgress = null;
        throw err;
      });
  }
  return fetchInProgress;
}

export function useConfig() {
  const [state, setState] = React.useState({
    config: cachedConfig || CONFIG_DEFAULTS,
    loading: !cachedConfig,
    error: null,
  });

  const reload = React.useCallback(() => {
    cachedConfig = null;
    fetchInProgress = null;
    setState(prev => ({ ...prev, loading: true, error: null }));
    loadConfig()
      .then(cfg => setState({ config: cfg, loading: false, error: null }))
      .catch(err => setState({ config: CONFIG_DEFAULTS, loading: false, error: err.message }));
  }, []);

  React.useEffect(() => {
    if (cachedConfig) return;
    loadConfig()
      .then(cfg => setState({ config: cfg, loading: false, error: null }))
      .catch(err => setState({ config: CONFIG_DEFAULTS, loading: false, error: err.message }));
  }, []);

  // Subscribe to same-tab busts (e.g. admin toggles on same page/tab)
  React.useEffect(() => {
    _subscribers.add(reload);
    return () => _subscribers.delete(reload);
  }, [reload]);

  // Listen for admin config saves from OTHER tabs via storage event
  React.useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'nr_config_updated') reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [reload]);

  const refetch = React.useCallback(() => {
    cachedConfig = null;
    fetchInProgress = null;
    setState(prev => ({ ...prev, loading: true, error: null }));
    loadConfig()
      .then(cfg => setState({ config: cfg, loading: false, error: null }))
      .catch(err => setState({ config: CONFIG_DEFAULTS, loading: false, error: err.message }));
  }, []);

  const getNrFormula = React.useCallback((pos) => {
    const key = `nr_formula_${pos.toLowerCase()}`;
    return state.config[key] ?? CONFIG_DEFAULTS[key] ?? {};
  }, [state.config]);

  const getSiteFeatures = React.useCallback(() => {
    return state.config.site_features ?? CONFIG_DEFAULTS.site_features;
  }, [state.config]);

  return { config: state.config, loading: state.loading, error: state.error, refetch, getNrFormula, getSiteFeatures };
}
